/**
 * `<DemoPhoneView/>` - what a staff member sees on a phone (#2824).
 *
 * The demo roster page shows it as a phone in the bottom-right corner. The
 * screen is the real member app, run in a frame at `/demo-phone/me`, signed
 * in as the person picked in "Viewing as". That picker sits above the phone,
 * outside it: it is the demo's control, not part of the member app. It is a look only: a tap that would
 * change something shows a short note. A lead's draft never shows here: it
 * shows after Publish roster.
 *
 * `<DemoPhoneDock/>` places it: a phone fixed to the bottom-right corner,
 * always folded at first, behind a "Phone view" button. "Hide" in the bar above the phone
 * folds it again. Opening it tells the guide ("phone-opened").
 */
import { useQueryClient } from "@tanstack/react-query";
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import { type DemoSessionStateResponse, useDemoSessionState } from "../../api/useDemoSessionState";
import { useTeamRangeRoster } from "../../api/useTeamRangeRoster";
import {
  DEMO_PHONE_PERSON_EVENT,
  DEMO_PHONE_SENT_MESSAGE,
  type PhoneSuggestion,
  demoPhoneSrc,
  emitGuideAction,
  getDemoPhoneScenarioLock,
  notifyDemoPhone,
  phoneRingToken,
  setDemoPhoneOpen,
  subscribeCloseDemoPhone,
  subscribeDemoPhoneScenarioLock,
  suggestionFor,
  takePendingPhonePerson,
} from "../../lib/demoPhone";
import { afterSidePanelsClose } from "../../lib/sidePanels";
import { Button, Skeleton } from "../ui";
import { subscribeDemoGuideAction, subscribeDemoPhoneScenarioStart } from "./guide/events";
import {
  HIDDEN_STYLE,
  onPhoneFrameLoad,
  phoneFrame,
  phoneFrameHolds,
  phoneFrameLoaded,
  setPhoneFrameSrc,
  warmPhoneFrame,
} from "./phoneFramePool";

/** Width of the phone the member app is laid out for; the frame scales down from it. */
const FRAME_WIDTH_PX = 360;
const SCALE = 0.78;
/** People after the shown one whose frames load in the background. */
const WARM_NEIGHBOURS = 1;

/**
 * The dock keeps ONE member-app frame alive for the whole demo (#2824), warm
 * and hidden while the phone is folded. A phone view that finds this host
 * draws an empty screen slot, and the dock lays the frame over it.
 */
interface PhoneFrameHost {
  slotRef: (el: HTMLElement | null) => void;
  onPerson: (id: string | null) => void;
}
const PhoneFrameHostContext = createContext<PhoneFrameHost | null>(null);

/** The frame's address for a person: the Calendar, or the Inbox after Publish roster. */
export function phoneFrameSrc(
  personId: string,
  suggestion: PhoneSuggestion | null,
  inbox: boolean,
): string {
  const src = demoPhoneSrc(personId, suggestion);
  return inbox ? src.replace("/me?", "/me/inbox?") : src;
}

function mark(name: string): void {
  try {
    performance.mark(name);
  } catch {
    // No performance marks here: nothing to record.
  }
}

/** Time from "the phone opened" to "its screen is ready", for the console and tests. */
function measureOpen(): void {
  try {
    const m = performance.measure(
      "demo-phone:open-to-ready",
      "demo-phone:open",
      "demo-phone:ready",
    );
    // Dev only: read the numbers in the console, or in performance.getEntriesByName.
    if (import.meta.env.DEV)
      console.info(`[demo-phone] open to ready: ${Math.round(m.duration)} ms`);
  } catch {
    // Missing mark: nothing to record.
  }
}

export interface DemoPhoneViewProps {
  teamId: string;
  from: string;
  to: string;
  /** Lead changes that wait for Publish roster. */
  changesWaiting: number;
  /** Who the phone shows first (a guide step asked while the phone was folded). */
  initialPerson?: string | null;
  /** Folds the phone; the bar above the phone then shows a Hide button. */
  onHide?: () => void;
}

export function DemoPhoneView({
  teamId,
  from,
  to,
  changesWaiting,
  initialPerson = null,
  onHide,
}: DemoPhoneViewProps) {
  const scale = SCALE;
  const query = useTeamRangeRoster(teamId, from, to, { memberView: true });
  const [picked, setPicked] = useState<string | null>(initialPerson);
  const members = query.data?.members ?? [];
  const session = useDemoSessionState();
  // A scenario plays: the phone stays on its one person and the picker is gone.
  const lock = useSyncExternalStore(
    subscribeDemoPhoneScenarioLock,
    getDemoPhoneScenarioLock,
    () => null,
  );
  const lockedId =
    lock?.memberId && members.some((m) => m.id === lock.memberId) ? lock.memberId : null;
  const personId =
    lockedId ??
    (picked && members.some((m) => m.id === picked) ? picked : (members[0]?.id ?? null));
  const person = members.find((m) => m.id === personId);
  const suggestion = suggestionFor(session.data, personId);
  const ringToken = personId ? phoneRingToken(personId) : null;
  const ringColor = ringToken ?? "rgb(var(--color-border-strong))";
  const host = useContext(PhoneFrameHostContext);
  const reportPerson = host?.onPerson;
  useEffect(() => {
    reportPerson?.(personId);
  }, [reportPerson, personId]);

  // The guide asks the phone to show one person.
  useEffect(() => {
    const onPerson = (e: Event) => {
      const id = (e as CustomEvent<{ membershipId?: string }>).detail?.membershipId;
      if (id) setPicked(id);
    };
    window.addEventListener(DEMO_PHONE_PERSON_EVENT, onPerson);
    return () => window.removeEventListener(DEMO_PHONE_PERSON_EVENT, onPerson);
  }, []);

  return (
    <div className="flex h-full w-full flex-col gap-1.5">
      <div
        // Not part of the member app: the demo's own control, outside the phone.
        // Light blue (the info token over a solid page colour), so nothing
        // behind it shows through. Dark text in both themes. The solid colour
        // is its own class: a colour inside a background-image list is invalid.
        className="shrink-0 rounded-2xl bg-bg bg-[linear-gradient(rgb(var(--color-info)/0.2),rgb(var(--color-info)/0.2))] px-2.5 py-2 text-fg shadow-lg ring-1 ring-[rgb(var(--color-info)/0.45)]"
        data-testid="demo-phone-controls"
      >
        <div className="flex items-center gap-1.5">
          {lock ? (
            <p
              className="flex min-w-0 flex-1 items-center gap-1.5 text-xs font-medium text-fg"
              data-testid="demo-phone-locked-person"
            >
              Viewing as
              <span className="min-w-0 truncate font-semibold">{person?.display_name ?? ""}</span>
            </p>
          ) : (
            <label className="flex min-w-0 flex-1 items-center gap-1.5 text-xs font-medium text-fg">
              Viewing as
              <select
                aria-label="Staff member"
                className="h-7 min-w-0 flex-1 rounded-lg border border-border bg-surface px-1.5 text-xs font-medium text-fg"
                value={personId ?? ""}
                onChange={(e) => setPicked(e.target.value)}
                data-testid="demo-phone-picker"
              >
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.display_name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {onHide ? (
            <button
              type="button"
              className="h-7 shrink-0 rounded-lg px-2 text-xs font-medium text-fg hover:bg-[rgb(var(--color-info)/0.25)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[rgb(var(--color-info))]"
              onClick={onHide}
              aria-label="Hide phone"
              data-testid="demo-phone-minimise"
            >
              Hide
            </button>
          ) : null}
        </div>
        {changesWaiting > 0 ? (
          <p className="pt-1.5 text-[11px] text-fg-muted" data-testid="demo-phone-waiting">
            {changesWaiting === 1 ? "1 change shows" : `${changesWaiting} changes show`} on the
            phone after Publish roster.
          </p>
        ) : null}
      </div>
      <div
        // The phone: a dark bezel, a notch and a status bar around the member app.
        className="flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-[28px] border-[5px] bg-bg shadow-2xl transition-colors duration-500 ease-out motion-reduce:transition-none"
        // The outline takes one calm colour per person, and fades to the next.
        style={{ borderColor: ringColor }}
        data-ring={ringToken ?? undefined}
        data-testid="demo-phone-view"
        data-tour="demo-phone"
      >
        <div className="relative flex h-6 shrink-0 items-center justify-between bg-bg px-5 text-[10px] font-semibold text-fg">
          <span>9:41</span>
          <span
            aria-hidden="true"
            className="absolute left-1/2 top-1 h-4 w-20 -translate-x-1/2 rounded-full transition-colors duration-500 ease-out motion-reduce:transition-none"
            style={{ backgroundColor: ringColor }}
          />
          <span aria-hidden="true">●●●</span>
        </div>
        <div className="min-h-0 flex-1">
          {personId ? (
            <div className="relative h-full w-full overflow-hidden" data-testid="demo-phone-screen">
              {host ? (
                <div
                  ref={host.slotRef}
                  className="absolute inset-0"
                  data-testid="demo-phone-slot"
                />
              ) : (
                <iframe
                  // A new pick loads the frame again, so it signs in as that person.
                  key={personId}
                  title="Member app"
                  src={demoPhoneSrc(personId, suggestion)}
                  className="absolute left-0 top-0 border-0 bg-bg"
                  style={{
                    width: `${FRAME_WIDTH_PX}px`,
                    height: `${100 / scale}%`,
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                  }}
                  data-testid="demo-phone-frame"
                />
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-2 p-3">
              <Skeleton className="h-12" />
              <Skeleton className="h-12" />
            </div>
          )}
        </div>
        <div className="flex h-5 shrink-0 items-center justify-center bg-bg" aria-hidden="true">
          <span className="h-1 w-24 rounded-full bg-fg/30" />
        </div>
        {person ? <span className="sr-only">Showing {person.display_name}'s phone.</span> : null}
      </div>
    </div>
  );
}

/** The member the picked change adds to the team, if it adds one. */
export function newJoinerOf(session: DemoSessionStateResponse | undefined): string | null {
  const change = session?.changes?.find((c) => c.id === session.variant);
  return change?.target?.kind === "new_member" ? (change.target.member_id ?? null) : null;
}

export interface DemoPhoneDockProps extends DemoPhoneViewProps {
  /** False outside a demo: the dock then draws only the page. */
  enabled: boolean;
  /** True until the roster on screen is published with no change waiting.
   * The Phone view button then stays but cannot open the phone. */
  locked?: boolean;
  children: ReactNode;
}

export function DemoPhoneDock({ enabled, locked = false, children, ...view }: DemoPhoneDockProps) {
  // Always starts folded behind the "Phone view" button.
  const [open, setOpenState] = useState(false);
  // `report`: tell the guide the phone opened ("phone-opened"). The page's own
  // open after Publish roster does not report: the guide's "See it on a phone"
  // step must still show, however late that open comes (#2824).
  const setOpen = useCallback((next: boolean, report = true) => {
    if (!next) {
      setOpenState(false);
      return;
    }
    // The phone never lands on top of an open side panel: the panel closes first.
    afterSidePanelsClose(() => {
      setOpenState(true);
      mark("demo-phone:open");
      if (report) emitGuideAction("phone-opened");
    });
  }, []);
  // The person the phone shows: asked for by the guide, or picked on the phone.
  const [wanted, setWanted] = useState<string | null>(null);
  // The guide asks the phone to fold before a step that is not about the phone.
  useEffect(() => subscribeCloseDemoPhone(() => setOpenState(false)), []);
  const reportedOpen = open && !locked;
  useEffect(() => {
    setDemoPhoneOpen(reportedOpen);
    return () => setDemoPhoneOpen(false);
  }, [reportedOpen]);
  // After Publish roster the frame lands on the Inbox.
  const [inbox, setInbox] = useState(false);
  const openerRef = useRef<HTMLButtonElement | null>(null);
  const queryClient = useQueryClient();
  // The guide asked for the phone before Publish: open it once Publish is done.
  // "silent": the ask came from Publish itself (it does not report the open).
  const askedWhileLocked = useRef<false | "report" | "silent">(false);
  const lockedRef = useRef(locked);
  lockedRef.current = locked;

  // The one warm frame: its address follows the person and the offered request.
  const roster = useTeamRangeRoster(view.teamId, view.from, view.to, {
    memberView: true,
    enabled,
  });
  const session = useDemoSessionState();
  const members = roster.data?.members ?? [];
  // A person asked for before the roster loaded is used as it is: no frame
  // loads for the first member only to be swapped away.
  // A person who joined in the picked change has no demo phone: the phone
  // starts on the first other member.
  const joiner = newJoinerOf(session.data);
  const personId =
    wanted && (members.length === 0 || members.some((m) => m.id === wanted))
      ? wanted
      : (members.find((m) => m.id !== joiner)?.id ?? null);
  const suggestion = suggestionFor(session.data, personId);
  const frameSrc = enabled && personId ? phoneFrameSrc(personId, suggestion, inbox) : null;
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const shown = open && !locked && slot !== null;
  const rect = useSlotRect(shown ? slot : null);

  // A phone that is open when the roster becomes locked folds. A request the
  // guide made while locked opens it once the lock lifts.
  useEffect(() => {
    if (locked) {
      setOpenState(false);
    } else if (askedWhileLocked.current) {
      const report = askedWhileLocked.current === "report";
      askedWhileLocked.current = false;
      setOpen(true, report);
    }
  }, [locked, setOpen]);

  useEffect(() => {
    if (!enabled) return;
    const early = takePendingPhonePerson();
    if (early) {
      setWanted(early);
      if (lockedRef.current) askedWhileLocked.current = "report";
      else setOpen(true);
    }
    // The guide asks for a person: open the phone if it is folded.
    const onPerson = (e: Event) => {
      const detail = (e as CustomEvent<{ membershipId?: string; open?: boolean }>).detail;
      const id = detail?.membershipId;
      if (!id) return;
      takePendingPhonePerson();
      setWanted(id);
      // A scenario start picks the person but leaves the phone folded.
      if (detail?.open === false) return;
      if (lockedRef.current) askedWhileLocked.current = "report";
      else setOpen(true);
    };
    // The member sent the offered request inside the frame.
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if ((e.data as { type?: string } | null)?.type !== DEMO_PHONE_SENT_MESSAGE) return;
      void queryClient.invalidateQueries();
      notifyDemoPhone();
      emitGuideAction("request-sent");
    };
    window.addEventListener(DEMO_PHONE_PERSON_EVENT, onPerson);
    window.addEventListener("message", onMessage);
    // Publish roster: the phone pops up on its Inbox, where the update is.
    const offAction = subscribeDemoGuideAction((action) => {
      if (action !== "published") return;
      setInbox(true);
      notifyDemoPhone();
      if (lockedRef.current) askedWhileLocked.current = "silent";
      else setOpen(true, false);
    });
    // A scenario starts: the frame goes back to its first screen, in the background.
    const offScenario = subscribeDemoPhoneScenarioStart(() => {
      setInbox(false);
      notifyDemoPhone();
    });
    return () => {
      window.removeEventListener(DEMO_PHONE_PERSON_EVENT, onPerson);
      window.removeEventListener("message", onMessage);
      offAction();
      offScenario();
    };
  }, [enabled, queryClient, setOpen]);

  // The one frame of the demo lives outside React (`phoneFramePool.ts`): it is
  // pointed at the person early, kept hidden while folded and laid over the
  // phone's screen while open. Opening a loaded frame is ready at once.
  useEffect(() => {
    if (!shown) return;
    if (frameSrc && phoneFrameLoaded(frameSrc)) {
      mark("demo-phone:ready");
      measureOpen();
      return;
    }
    return onPhoneFrameLoad(() => {
      mark("demo-phone:ready");
      measureOpen();
    });
  }, [shown, frameSrc]);
  // Once the phone's screen is ready, the next people in the picker load
  // behind it, so picking one of them shows a frame that is already signed in.
  const memberIds = members.map((m) => m.id).join(",");
  useEffect(() => {
    if (!shown || !frameSrc || !personId) return;
    const ids = memberIds.split(",").filter(Boolean);
    const at = ids.indexOf(personId);
    const next = ids.slice(at + 1, at + 1 + WARM_NEIGHBOURS);
    if (next.length === 0) return;
    const warm = () => {
      for (const id of next) {
        const src = phoneFrameSrc(id, suggestionFor(session.data, id), inbox);
        if (!phoneFrameHolds(src)) warmPhoneFrame(src);
      }
    };
    if (phoneFrameLoaded(frameSrc)) {
      const t = window.setTimeout(warm, 300);
      return () => window.clearTimeout(t);
    }
    let t = 0;
    const off = onPhoneFrameLoad((src) => {
      if (src === frameSrc) t = window.setTimeout(warm, 300);
    });
    return () => {
      off();
      window.clearTimeout(t);
    };
  }, [shown, frameSrc, personId, memberIds, inbox, session.data]);
  useLayoutEffect(() => {
    if (!frameSrc) return;
    // The person's own frame comes first, so the styling below lands on it.
    setPhoneFrameSrc(frameSrc);
    const el = phoneFrame();
    if (shown && rect) {
      Object.assign(el.style, {
        ...HIDDEN_STYLE,
        left: `${rect.left}px`,
        top: `${rect.top}px`,
        width: `${rect.width > 0 ? rect.width / SCALE : FRAME_WIDTH_PX}px`,
        height: `${rect.height / SCALE}px`,
        transform: `scale(${SCALE})`,
        transformOrigin: "top left",
        visibility: "visible",
        pointerEvents: "auto",
      });
      el.setAttribute("aria-hidden", "false");
      el.tabIndex = 0;
    } else {
      Object.assign(el.style, HIDDEN_STYLE);
      el.setAttribute("aria-hidden", "true");
      el.tabIndex = -1;
    }
    // The frame lives outside React: when the dock leaves the page (a page
    // change), hide it, or the member screen stays on top of the new page.
    return () => {
      Object.assign(el.style, HIDDEN_STYLE);
      el.setAttribute("aria-hidden", "true");
      el.tabIndex = -1;
    };
  }, [frameSrc, shown, rect]);

  const host = useRef<PhoneFrameHost>({ slotRef: setSlot, onPerson: setWanted });
  if (!enabled) return <>{children}</>;
  return (
    <PhoneFrameHostContext.Provider value={host.current}>
      <div
        // The open phone floats over the page. It never shifts the page, so
        // the page's buttons stay where they were.
        data-testid="demo-phone-room"
      >
        {children}
      </div>
      {open && !locked ? (
        <aside
          aria-label="Phone view"
          className="fixed bottom-4 right-4 z-40 flex flex-col items-end gap-1.5"
          data-testid="demo-phone-floating"
        >
          {/* The phone stays below the roster's action row (Solve, Publish) on a
              laptop-height window: 26rem clear at the top, 440px tall at least. */}
          <div
            className="h-[min(680px,max(440px,calc(100vh-26rem)),calc(100vh-2rem))] w-[290px] max-w-[calc(100vw-2rem)]"
            data-testid="demo-phone-box"
          >
            <DemoPhoneView
              {...view}
              initialPerson={wanted}
              onHide={() => {
                setOpen(false);
                window.setTimeout(() => openerRef.current?.focus(), 0);
              }}
            />
          </div>
        </aside>
      ) : (
        <Button
          ref={openerRef}
          type="button"
          size="sm"
          className="fixed bottom-4 right-4 z-40 shadow-lg"
          onClick={() => setOpen(true)}
          disabled={locked}
          aria-describedby={locked ? "demo-phone-locked-note" : undefined}
          data-testid="demo-phone-toggle"
          data-tour="demo-phone-toggle"
        >
          {locked ? "Phone view · after Publish roster" : "Phone view"}
        </Button>
      )}
      {locked ? (
        <span id="demo-phone-locked-note" className="sr-only">
          The phone shows after Publish roster.
        </span>
      ) : null}
    </PhoneFrameHostContext.Provider>
  );
}

/**
 * The on-screen box of an element, kept current while it shows. A resize
 * observer and scroll listener miss a slot that moves without changing size
 * (a layout shift above it, a scrollbar appearing, a guide card or a
 * transition moving an ancestor), so a frame loop also re-reads the box and
 * the frame never drifts from its slot (#2824).
 */
type SlotRect = { left: number; top: number; width: number; height: number };
function useSlotRect(el: HTMLElement | null): SlotRect | null {
  const [rect, setRect] = useState<SlotRect | null>(null);
  useLayoutEffect(() => {
    if (!el) {
      setRect(null);
      return;
    }
    const read = () => {
      const r = el.getBoundingClientRect();
      setRect((prev) =>
        prev &&
        prev.left === r.left &&
        prev.top === r.top &&
        prev.width === r.width &&
        prev.height === r.height
          ? prev
          : { left: r.left, top: r.top, width: r.width, height: r.height },
      );
    };
    read();
    window.addEventListener("resize", read);
    document.addEventListener("scroll", read, { capture: true, passive: true });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(read);
    observer?.observe(el);
    observer?.observe(document.body);
    let raf = 0;
    const loop = () => {
      read();
      raf = window.requestAnimationFrame(loop);
    };
    if (typeof window.requestAnimationFrame === "function")
      raf = window.requestAnimationFrame(loop);
    return () => {
      window.removeEventListener("resize", read);
      document.removeEventListener("scroll", read, { capture: true });
      observer?.disconnect();
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [el]);
  return rect;
}
